import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams, Redirect } from "react-router-dom";
import { Row, Col } from "react-flexbox-grid";
import { useQuery } from "@apollo/client";

import {
  ALL_WINES_PAGINATION_LIMIT,
  FETCH_POLICY_CACHE_ONLY,
  I18N_NAMESPACES,
  WINE_LIST_SLUGS,
  AUTO_HIDE_SCROLL_BUFFER_IN_PX,
  FETCH_POLICY_NETWORK_ONLY,
  FETCH_POLICY_CACHE_FIRST,
  FETCH_POLICY_NO_CACHE,
} from "../../helpers/constants";
import {
  trackingEvents,
  formatTrackingEventDataFilteredWines,
  trackingEventSearchedWineOptions,
  trackingEventSortedWineOptions,
  useTrackEvent,
} from "../../helpers/Analytics";
import urlPatterns from "../../urls";
import { useDeviceSize } from "../../helpers/hooks";
import { logError } from "../../helpers/logging";
import { getWineSortingOptions } from "../../helpers/wineSorting";
import { topHeroContainerHeight } from "../../styles/variables";
import SearchInput from "../../components/atoms/SearchInput";
import { Container } from "../../components/atoms/Container";
import SelectDropdown from "../../components/atoms/SelectDropdown";
import FilterWinesToggleButton from "../../components/atoms/FilterWinesToggleButton";
import OptionsSearchFilterContainer from "../../components/atoms/OptionsSearchFilterContainer";
import SearchAndFilter from "../../components/atoms/SearchAndFilter";
import PageMeta from "../../components/molecules/PageMeta";
import { Hero, HeroTitle } from "../../components/molecules/Hero";
import BuildYourOwnBanner from "../../components/molecules/BuildYourOwnBanner";
import AllWinesOfWineList from "../../components/organisms/AllWinesOfWineList";
import FilterWinesTray from "../../components/organisms/FilterWinesTray";
import WineDetailsTray from "../../components/organisms/WineDetailsTray";
import WineListsSidePanel from "../../components/organisms/WineListsSidePanel";

import {
  GET_ALL_WINES_BY_WINE_LIST_WITH_COUNT,
  FETCH_MORE_ALL_WINES_BY_WINE_LIST,
  GET_AUTH,
  GET_MEMBER_SHOPPING_CART,
  GET_WINE_LIST,
  GET_ALL_WINES_BY_WINE_LIST_MATCH_COUNT,
} from "../../graphql/queries";
import { AllWinesByWineListWithCount } from "../../types/__generated__/AllWinesByWineListWithCount";
import { SelectDropdownHolder, SelectorsHolder } from "../Wines/style";
import { getAuth } from "../../types/__generated__/getAuth";
import { MemberShoppingCart } from "../../types/__generated__/MemberShoppingCart";
import { useAppContext, useLayoutContext } from "../../components/organisms/Layout/context";
import { WineList as GetWineList } from "../../types/__generated__/WineList";
import { isLoggedIn } from "../../helpers/auth";
import { AllWinesByWineListMatchCount } from "../../types/__generated__/AllWinesByWineListMatchCount";
import { GetCredit, GetCreditVariables } from "../../types/__generated__/GetCredit";
import { GET_CREDIT } from "../../graphql/resolvers/member";
import {
  ContainerTop,
  OptionButton,
  OptionsButtons,
  StyledWinesSection,
  FilterWinesWrapper,
  SmUpSearchContainer,
  SearchAndFilterResultsContainer,
  SearchAndFilterResultsInnerContainer,
  SearchResultsLabel,
  ClearAll,
  ChooseFreeBottleFooter,
  ChooseFreeBottleTitle,
  ChooseFreeBottleTitleContainer,
} from "./style";

interface WineListFilterProps {
  countryIds?: number[];
  cuisineIds?: number[];
  name?: string | null;
  orderBy?: string;
  wineAcidityId?: number;
  wineBodyId?: number;
  wineClassIds?: number[];
  wineFruitDrivenId?: number;
  wineListSlug?: string;
  winePriceRangeIds?: number[];
  wineProductionMethodIds?: number[];
  wineRegionIds?: number[];
  wineSweetnessId?: number;
  wineTanninId?: number;
  wineTypeIds?: number[];
  wineOakyId?: number;
  wineAlcoholRange?: number[];
}

const WineList = () => {
  const location = useLocation<{
    shouldOpenBoxAndCartTray?: boolean;
    openWineDetailsTray?: boolean;
    selectedFilters?: Partial<WineListFilterProps>;
    orderBy?: string;
    clearFilters?: boolean;
  }>();
  const { setShowHeaderBarFromChild, setShouldOpenBoxAndCartTray } = useLayoutContext();
  const { t } = useTranslation([I18N_NAMESPACES.WINE_PAGES, I18N_NAMESPACES.COMMON]);
  const { isSmall } = useDeviceSize();
  const history = useHistory();
  const { minBottlesForFreeShipping } = useAppContext();
  const trackEvent = useTrackEvent();

  const { wineListSlug } = useParams<{ wineListSlug: string }>();

  const [showHeaderBar, setShowHeaderBar] = useState<boolean>(true);
  const [openFiltersTray, setOpenFiltersTray] = useState<boolean>(false);
  const [openBookmarksTray, setOpenBookmarksTray] = useState<boolean>(false);
  const [selectedWineSlug, setSelectedWineSlug] = useState<string>("");
  const [yValInflectionPoint, setYValInflectionPoint] = useState<number>(0);
  const [isFetchingMore, setIsFetchingMore] = useState<boolean>(false);
  const [openWineDetailsTray, setOpenWineDetailsTray] = useState<boolean>(
    location.state?.openWineDetailsTray ?? false
  );
  const [wineToBeBookmarkedId, setWineToBeBookmarkedId] = useState<number>();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);

  const creditId = parseInt(searchParams.get("creditId") || "", 10);
  const { data: creditData } = useQuery<GetCredit, GetCreditVariables>(GET_CREDIT, {
    variables: {
      id: creditId,
    },
    skip: !creditId,
  });

  const [filters, setFilters] = useState<WineListFilterProps>({
    // Use search from the URL if any
    name: searchParams.get("search"),

    // Use order by from the location state if any, default to newest first
    orderBy: location.state?.orderBy || "CREATED_DATE_DESC",

    ...location.state?.selectedFilters,
  });

  const { data: authData } = useQuery<getAuth>(GET_AUTH, {
    fetchPolicy: FETCH_POLICY_CACHE_ONLY,
  });

  const { data: memberShoppingCart } = useQuery<MemberShoppingCart>(GET_MEMBER_SHOPPING_CART);

  /**
   * Returns the filter to be used in the AllWinesByWineList query.
   * Removes all filters with empty values as the AllWinesByWineList endpoint filters by empty values as well.
   * Overrides the price range filter with the credit's wine price range id if any.
   */
  const getAllWinesByWineListQueryFilters = useCallback((): WineListFilterProps => {
    let sanitizedFilters = { ...filters };

    Object.keys(sanitizedFilters).forEach((key) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const val = (sanitizedFilters as any)[key];
      // eslint-disable-next-line no-param-reassign, @typescript-eslint/no-explicit-any
      if (!val || (Array.isArray(val) && !val?.length)) delete (sanitizedFilters as any)[key];
    });

    if (creditData?.credit?.winePriceRange?.id) {
      sanitizedFilters = {
        ...sanitizedFilters,
        winePriceRangeIds: [creditData.credit.winePriceRange.id],
      };
    }

    return sanitizedFilters;
  }, [filters, creditData?.credit]);

  // Fetch wines in the selected wine list and matching the selected filters
  const {
    loading: winesLoading,
    data: winesData,
    fetchMore: fetchMoreWines,
  } = useQuery<AllWinesByWineListWithCount>(GET_ALL_WINES_BY_WINE_LIST_WITH_COUNT, {
    variables: {
      wineListSlug,
      limit: ALL_WINES_PAGINATION_LIMIT,
      offset: 0,
      ...getAllWinesByWineListQueryFilters(),
    },

    fetchPolicy: FETCH_POLICY_NETWORK_ONLY,

    // Use cache first nextFetchPolicy to avoid refetching previous page on fetchMore
    nextFetchPolicy: FETCH_POLICY_CACHE_FIRST,
  });

  const { loading: wineListLoading, data: wineListData } = useQuery<GetWineList>(GET_WINE_LIST, {
    variables: {
      memberId: isLoggedIn() && authData?.auth?.memberId,
      slug: wineListSlug,
    },
  });

  const { data: matchCountData, loading: matchCountLoading } = useQuery<AllWinesByWineListMatchCount>(
    GET_ALL_WINES_BY_WINE_LIST_MATCH_COUNT,
    {
      variables: {
        wineListSlug,
        ...getAllWinesByWineListQueryFilters(),
      },

      // Only query match count if the filters tray is opened
      skip: !openFiltersTray,

      // Disable cache to avoid issue with nested objects described here
      // https://github.com/apollographql/apollo-client/issues/3234
      fetchPolicy: FETCH_POLICY_NO_CACHE,
    }
  );

  useEffect(() => {
    // 'clearFilters' is set to true when navigated from Header's All wines and New Releases.
    if (location.state?.clearFilters) {
      setFilters({});
    }
  }, [location.state?.clearFilters]);

  useEffect(() => {
    // The selectedFilters state allows to redirect to the wine list page with preselected filters
    // e.g. when redirecting to the list of wines corresponding to a given badge
    if (location.state?.selectedFilters) {
      setFilters(location.state.selectedFilters);
      trackApplyFilters(location.state?.selectedFilters);
    }
  }, [location.state?.selectedFilters]);

  useEffect(() => {
    // Show/hide the header bar in the layout
    setShowHeaderBarFromChild(showHeaderBar);
  }, [showHeaderBar]);

  useEffect(() => {
    // Update search when user clicks on a wine tag
    const searchQueryParam = searchParams.get("search");
    if (searchQueryParam && searchQueryParam !== filters.name) {
      trackEvent(trackingEvents.TRACKING_EVENT_SEARCHED_WINES, {
        [trackingEventSearchedWineOptions.SEARCH_PHRASE]: searchQueryParam,
      });
      setFilters({ ...filters, name: searchQueryParam });
    }
  }, [location.search]);

  useEffect(() => {
    // 'shouldOpenBoxAndCartTray' is set to true when navigating from "Build your own box"
    if (location.state?.shouldOpenBoxAndCartTray) {
      setShouldOpenBoxAndCartTray(location.state.shouldOpenBoxAndCartTray);
    }
  }, [location.state?.shouldOpenBoxAndCartTray]);

  useEffect(() => {
    // Show/hide header on scroll
    window.addEventListener("scroll", handleScroll);

    // Cleanup/unmount
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [yValInflectionPoint]);

  /**
   * Handler for scroll event listeners, to set state to show or hide Header bar.
   */
  const handleScroll = () => {
    const yValue = +window.scrollY;

    // To fix the flickering issue while slow scrolling in Google Chrome,
    // we only show/hide header, search and filter bar,
    // after customers scroll at least 250px.
    if (yValue > yValInflectionPoint + AUTO_HIDE_SCROLL_BUFFER_IN_PX) {
      setYValInflectionPoint(yValue);
      setShowHeaderBar(false);
    } else if (yValue < yValInflectionPoint - AUTO_HIDE_SCROLL_BUFFER_IN_PX) {
      setYValInflectionPoint(yValue);
      setShowHeaderBar(true);
    }
  };

  /**
   * Scroll the window to top of page
   */
  const scrollToTopHero = () => {
    window.scrollTo(window.scrollX, topHeroContainerHeight);
  };

  /**
   * Count how many filers were selected
   * @param includeOrderBy Include the orderby in the count
   */
  const numberOfFilterSelected = useCallback(
    (includeOrderBy: boolean = false) => {
      let selectedFiltersAmount = 0;
      Object.keys(filters)
        .filter((property) =>
          !includeOrderBy ? property !== "name" && property !== "orderBy" : property !== "name"
        )
        .forEach((property) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const val = (filters as any)[property];
          if (Array.isArray(val)) {
            if (property === "wineAlcoholRange") {
              // wineAlcoholRange is a range filter so only counts for 1 even though it contains 2 values
              selectedFiltersAmount += val.length > 0 ? 1 : 0;
            } else {
              selectedFiltersAmount += val.length || 0;
            }
          } else if (val) {
            selectedFiltersAmount += 1;
          }
        });
      return selectedFiltersAmount;
    },
    [filters]
  );

  /**
   * Update filters with the search phrase and add the search phrase to the URL query params
   * @param searchPhrase
   */
  const handleSearch = (searchPhrase: string) => {
    const searchQueryParam = searchParams.get("search");
    if (searchPhrase) {
      if (searchQueryParam !== searchPhrase) {
        searchParams.set("search", searchPhrase);
        history.push({
          pathname: urlPatterns.WINE_LIST(WINE_LIST_SLUGS.ALL_WINES),
          search: `?${searchParams}`,
        });
      }

      if (searchPhrase !== filters.name) {
        trackEvent(trackingEvents.TRACKING_EVENT_SEARCHED_WINES, {
          [trackingEventSearchedWineOptions.SEARCH_PHRASE]: searchPhrase,
        });
      }
      setFilters({ ...filters, name: searchPhrase });
    } else {
      searchParams.delete("search");
      history.push({
        pathname: urlPatterns.WINE_LIST(WINE_LIST_SLUGS.ALL_WINES),
        search: `?${searchParams}`,
      });
      setFilters({ ...filters, name: "" });
    }

    if (isSmall) {
      scrollToTopHero();
    }
  };

  /**
   * Update filters with the new order by
   * @param orderBy
   */
  const handleSortingListSelection = (orderBy: string) => {
    trackEvent(trackingEvents.TRACKING_EVENT_SORTED_WINES, {
      [trackingEventSortedWineOptions.ORDER_BY]: orderBy,
    });
    setFilters({
      ...filters,
      orderBy,
    });
  };

  /**
   * Show/hide the wine filter side tray
   */
  const handleToggleFilterWines = () => {
    // if tray is closing, track event with filter data selected.
    if (openFiltersTray) {
      trackApplyFilters(filters);
    }
    setOpenFiltersTray((prevValue) => !prevValue);
    setShowHeaderBar(true);
  };

  /**
   * Show/hide the bookmark tray for the given wine
   * @param wineId
   */
  const handleToggleBookmarksTray = (wineId: number) => {
    setOpenBookmarksTray((prevValue) => !prevValue);
    setWineToBeBookmarkedId(wineId);
    setShowHeaderBar(true);
  };

  /**
   * Clear all filters
   */
  const handleClearAll = () => {
    setFilters({});
    if (isSmall) {
      scrollToTopHero();
    }
  };

  /**
   * Show/hide the wine details tray for the given wine
   * @param newSelectedWineSlug
   */
  const handleToggleWineDetailsTray = (newSelectedWineSlug: string) => {
    setOpenWineDetailsTray((prevValue) => !prevValue);
    setSelectedWineSlug(newSelectedWineSlug);
    setShowHeaderBar(true);
  };

  /**
   * Set selected filters
   * @param selectedFilters
   */
  const handleFiltersChange = (selectedFilters: WineListFilterProps) => {
    setFilters(selectedFilters);

    if (isSmall) {
      scrollToTopHero();
    }
  };

  /**
   * Fetch more wines
   */
  const handleFetchMoreRequested = async () => {
    // Do not run if we are already fetching more wines
    if (!isFetchingMore) {
      setIsFetchingMore(true);
      await fetchMoreWines({
        variables: {
          wineListSlug,
          limit: ALL_WINES_PAGINATION_LIMIT,
          offset: winesData?.allWinesByWineList?.length,
          ...getAllWinesByWineListQueryFilters(),
        },
        query: FETCH_MORE_ALL_WINES_BY_WINE_LIST,
      });
      setIsFetchingMore(false);
    }
  };

  /**
   * Show/hide the header bar
   * @param isShown
   */
  const handleSetHeaderDisplay = (isShown: boolean = true) => {
    setShowHeaderBar(isShown);
  };

  /**
   * Tracks the apply filters event with all filters data if 1 or more filters are set.
   *
   * @param {WineListFilterProps} appliedFilters
   */
  const trackApplyFilters = (appliedFilters?: WineListFilterProps) => {
    if (appliedFilters) {
      const filtersTrackingMeta = formatTrackingEventDataFilteredWines(appliedFilters);
      const containsAllNullValues = Object.values(filtersTrackingMeta).every((filter) => filter === null);

      if (!containsAllNullValues) {
        trackEvent(trackingEvents.TRACKING_EVENT_FILTERED_WINES, filtersTrackingMeta);
      }
    }
  };

  /**
   * Get sorting options from translation files
   */
  const sortingOptions = useMemo(() => getWineSortingOptions(t, !!authData?.auth?.memberId), [authData, t]);

  if (!wineListLoading && !wineListData?.wineList) {
    logError("Wine List not found.");
    return <Redirect to={urlPatterns.NOT_FOUND} />;
  }

  return (
    <>
      <PageMeta
        title={t("winePages:pageMeta.wineList.title", {
          minBottlesForFreeShipping,
        })}
        description={t("winePages:pageMeta.wineList.description")}
      />
      <Hero>
        <Container>
          <Row middle="md">
            <Col lg={9}>
              <HeroTitle>{t("common:wines")}</HeroTitle>
            </Col>
            <Col lg={3}>
              <SmUpSearchContainer>
                <SearchInput initialValue={filters.name} onChange={handleSearch} />
              </SmUpSearchContainer>
            </Col>
          </Row>
        </Container>
      </Hero>
      <BuildYourOwnBanner />
      <StyledWinesSection>
        <OptionsSearchFilterContainer isShown={showHeaderBar}>
          <Container>
            <ContainerTop>
              <OptionsButtons>
                <OptionButton to={urlPatterns.WINE_LIST(WINE_LIST_SLUGS.ALL_WINES)} selected>
                  {t("common:allWines")}
                </OptionButton>
                <OptionButton to={urlPatterns.WINES} selected={false}>
                  {t("common:collections")}
                </OptionButton>
              </OptionsButtons>
              <SelectorsHolder>
                <SelectDropdownHolder>
                  <SelectDropdown
                    title={t("winePages:sort")}
                    onChange={handleSortingListSelection}
                    setValue={filters.orderBy}
                    dropdownItems={sortingOptions}
                    hasCheckIcon={false}
                    addFilterStyles
                  />
                </SelectDropdownHolder>
                <FilterWinesWrapper>
                  <FilterWinesToggleButton
                    toggleFilterWines={handleToggleFilterWines}
                    numberOfSelectedFilters={numberOfFilterSelected(false)}
                  />
                </FilterWinesWrapper>
              </SelectorsHolder>
              <SearchAndFilter
                name={filters.name}
                handleSearch={handleSearch}
                handleFilterClick={handleToggleFilterWines}
                numberOfFiltersSelected={numberOfFilterSelected(true)}
              />
            </ContainerTop>
          </Container>
          {(numberOfFilterSelected(true) || filters.name) && (
            <SearchAndFilterResultsContainer>
              <Container>
                <SearchAndFilterResultsInnerContainer>
                  <SearchResultsLabel>
                    {t("common:item", {
                      count: winesData?.allWinesByWineListCount || 0,
                    })}
                  </SearchResultsLabel>
                  <ClearAll onClick={handleClearAll}>{t("common:clear")}</ClearAll>
                </SearchAndFilterResultsInnerContainer>
              </Container>
            </SearchAndFilterResultsContainer>
          )}
        </OptionsSearchFilterContainer>
        <Container>
          <AllWinesOfWineList
            wineList={wineListData?.wineList}
            nameFilter={filters.name ?? ""}
            numberOfFilterSelected={numberOfFilterSelected(false)}
            onSetHeaderDisplay={handleSetHeaderDisplay}
            onToggleBookmarksTray={handleToggleBookmarksTray}
            onToggleWineDetailsTray={handleToggleWineDetailsTray}
            onToggleAddBottleTray={null}
            wineCount={winesData?.allWinesByWineListCount}
            wines={winesData?.allWinesByWineList}
            onFetchMoreRequested={handleFetchMoreRequested}
            memberShoppingCart={memberShoppingCart}
            isLoading={winesLoading || isFetchingMore}
            addBottleToCredit={creditData?.credit || undefined}
          />
          {wineToBeBookmarkedId && openBookmarksTray && (
            <WineListsSidePanel
              isInBoxAndCartTray={false}
              className="side-panel"
              handleOffCanvasChange={handleToggleBookmarksTray}
              memberId={authData?.auth?.memberId}
              wine={wineToBeBookmarkedId}
              history={history}
              // Nothing to do, the apollo cache will be updated with the new "bookmarked" value
              // for the wine
              callbackAfterBookmarkingWine={() => {}}
            />
          )}
        </Container>
      </StyledWinesSection>
      {openWineDetailsTray && (
        <WineDetailsTray
          handleToggleBottleInfoTray={handleToggleWineDetailsTray}
          slug={selectedWineSlug}
          memberShoppingCartQuery={memberShoppingCart}
          addBottleToCredit={creditData?.credit || undefined}
        />
      )}
      {openFiltersTray && (
        <FilterWinesTray
          toggleFilterWines={handleToggleFilterWines}
          onChange={handleFiltersChange}
          selectedWinePriceRangeIds={filters.winePriceRangeIds}
          selectedWineClassIds={filters.wineClassIds}
          selectedWineProductionMethodIds={filters.wineProductionMethodIds}
          selectedCountryIds={filters.countryIds}
          selectedCuisineIds={filters.cuisineIds}
          selectedWineRegionIds={filters.wineRegionIds}
          selectedWineTypeIds={filters.wineTypeIds}
          selectedWineAlcoholRange={filters.wineAlcoholRange}
          selectedWineSweetnessId={filters.wineSweetnessId}
          selectedWineTanninId={filters.wineTanninId}
          selectedWineBodyId={filters.wineBodyId}
          selectedWineFruitDrivenId={filters.wineFruitDrivenId}
          selectedWineAcidityId={filters.wineAcidityId}
          selectedWineListSlug={filters.wineListSlug}
          selectedWineOakyId={filters.wineOakyId}
          selectedOrderBy={filters.orderBy}
          selectedName={filters.name}
          totalMatchCount={winesData?.allWinesByWineListCount}
          matchCountData={matchCountData}
          matchCountLoading={matchCountLoading}
          // Hides the price range filter if the user is choosing a bottle for a choose free bottle credit
          showPriceRangesFilter={creditData?.credit === undefined}
        />
      )}
      {creditData?.credit && (
        <ChooseFreeBottleFooter>
          <Container>
            <ChooseFreeBottleTitleContainer>
              <ChooseFreeBottleTitle>
                {t("common:chooseFreeBottle", {
                  priceRange: creditData.credit.winePriceRange?.winePriceRangeValue?.name,
                })}
              </ChooseFreeBottleTitle>
            </ChooseFreeBottleTitleContainer>
          </Container>
        </ChooseFreeBottleFooter>
      )}
    </>
  );
};

export default WineList;
